import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageLayout from "@/components/PageLayout";
import SEO from "@/components/SEO";
import FAQSchema from "@/components/FAQSchema";
import ContactModal from "@/components/new-landing/ContactModal";
import LogoCarousel from "@/components/new-landing/LogoCarousel";
import { trackEvent } from "@/utils/analytics";

const NAVY = "#002178";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const differentiators = [
  {
    title: "Embedded, not advisory",
    description: "We join your team, attend standups, drive execution.",
  },
  {
    title: "Team, not solo contractor",
    description: "Senior product leaders with partner oversight on every engagement.",
  },
  {
    title: "Outcomes, not hours",
    description: "Accountable for measurable business impact.",
  },
  {
    title: "AI-native execution",
    description: "Every workflow powered by AI. Faster, more thorough, better decisions.",
  },
];

const faqs = [
  {
    question: "How does FractionalCPO work?",
    answer:
      "We start with an assessment to identify the highest-impact opportunities. Then we embed senior product leaders who drive execution and deliver results. Every engagement has direct partner oversight.",
  },
  {
    question: "What industries does FractionalCPO serve?",
    answer:
      "Technology companies across SaaS, enterprise platforms, marketplaces, and fintech. Our sweet spot is PE-backed companies, but our methodology applies wherever product drives revenue.",
  },
  {
    question: "How is FractionalCPO different from hiring a full-time CPO?",
    answer:
      "Speed and de-risked execution. We start in weeks, not months. You get a team with a decade of playbooks, not a single hire who needs to ramp up. And you only pay for the engagement, not a full-time salary plus equity.",
  },
  {
    question: "Where is FractionalCPO based?",
    answer:
      "We're based in North America and work with technology companies across the continent. All engagements are embedded, whether on-site or remote.",
  },
  {
    question: "How many people does FractionalCPO assign to an engagement?",
    answer:
      "It depends on scope. We typically embed a team of senior product leaders with direct partner oversight. You get the depth of a team, not a solo contractor.",
  },
];

const About = () => {
  const [isContactOpen, setIsContactOpen] = useState(false);

  return (
    <PageLayout>
      <SEO
        title="About FractionalCPO | Who We Are"
        description="A firm of senior product leaders with a decade of embedded product leadership. We deliver financial results, not slides."
        path="/about"
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://fractionalcpo.com/" },
                { "@type": "ListItem", "position": 2, "name": "About", "item": "https://fractionalcpo.com/about" }
              ]
            },
            {
              "@type": "Organization",
              "name": "FractionalCPO",
              "url": "https://fractionalcpo.com",
              "description": "Revenue growth through product leadership. A firm of senior product leaders providing interim product leadership for technology companies.",
              "founder": { "@id": "https://fractionalcpo.com/#vahid-jozi" },
              "member": [
                { "@id": "https://fractionalcpo.com/#vahid-jozi" },
                { "@id": "https://fractionalcpo.com/#courtney-cunningham" }
              ],
              "areaServed": "North America"
            },
            {
              "@type": "Person",
              "@id": "https://fractionalcpo.com/#vahid-jozi",
              "name": "Vahid Jozi",
              "jobTitle": "Fractional Chief Product Officer, Founder",
              "url": "https://vahidjozi.com",
              "email": "vahid@fractionalcpo.com",
              "worksFor": {
                "@type": "Organization",
                "name": "FractionalCPO",
                "url": "https://fractionalcpo.com"
              },
              "sameAs": [
                "https://www.linkedin.com/in/vahidjozi/",
                "https://vahidjozi.com"
              ]
            },
            {
              "@type": "Person",
              "@id": "https://fractionalcpo.com/#courtney-cunningham",
              "name": "Courtney Cunningham",
              "jobTitle": "Operating Partner",
              "worksFor": {
                "@type": "Organization",
                "name": "FractionalCPO",
                "url": "https://fractionalcpo.com"
              },
              "sameAs": [
                "https://www.linkedin.com/in/courtneycunningham13/"
              ]
            }
          ]
        }}
      />

      {/* Hero */}
      <section className="py-24 md:py-40 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <motion.p
            {...fadeUp}
            className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-8 font-medium"
          >
            About Us
          </motion.p>

          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-[clamp(2.5rem,8vw,6rem)] font-light leading-[0.95] tracking-[-0.03em] mb-12"
          >
            Who We Are
          </motion.h1>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-3xl"
          >
            <p className="text-xl md:text-2xl leading-relaxed text-black/70">
              A firm of senior product leaders. A decade of embedded product leadership. Financial results, not slides.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Companies We've Worked With */}
      <section className="py-12 md:py-16 bg-[#F5F5F0]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 mb-6">
          <p className="text-sm uppercase tracking-[0.3em] text-[#002178] font-medium">
            Companies We've Worked With
          </p>
        </div>
        <LogoCarousel variant="light" />
      </section>

      {/* Testimonial */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-[900px] mx-auto">
          <motion.div {...fadeUp} className="relative pl-12 md:pl-16">
            <span className="absolute top-0 left-0 text-[#0047FF] text-[5rem] md:text-[6rem] font-serif leading-none select-none pointer-events-none">"</span>
            <p className="text-[clamp(1.25rem,3vw,1.75rem)] font-light leading-[1.5] mb-8 text-[#1a1a1a]/80 relative z-10">
              Love what you guys are doing since my restructure. Feedback is positive.
            </p>
            <div className="flex items-center gap-2 text-sm">
              <span className="font-medium text-[#002178]">CEO</span>
              <span className="text-black/30">&bull;</span>
              <span className="text-black/50">PE-Backed Platform</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F5F5F0]">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-2 gap-16 md:gap-24 items-start">
            <div>
              <motion.p
                {...fadeUp}
                className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-4 font-medium"
              >
                Our Approach
              </motion.p>
              <motion.h2
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-8"
              >
                Embedded where it{" "}
                <span className="text-[#002178] font-medium">matters.</span>
              </motion.h2>
            </div>

            <div>
              <motion.p
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-lg text-black/60 leading-relaxed mb-6"
              >
                We embed teams of senior product leaders with direct partner oversight. We don't advise from the sidelines. We take interim leadership roles, make decisions, ship product, and own outcomes.
              </motion.p>
              <motion.p
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-lg text-black/60 leading-relaxed"
              >
                Our 5% methodology identifies the small set of changes that drive outsized business impact.
              </motion.p>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <motion.p
            {...fadeUp}
            className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-4 font-medium"
          >
            The Difference
          </motion.p>
          <motion.h2
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-16"
          >
            What Makes Us{" "}
            <span className="text-[#002178] font-medium">Different</span>
          </motion.h2>

          <div className="grid sm:grid-cols-2 gap-8">
            {differentiators.map((item, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ delay: i * 0.1 }}
                className="border border-black/10 p-8 hover:border-[#002178]/30 transition-colors"
              >
                <h3 className="text-xl font-medium mb-3">{item.title}</h3>
                <p className="text-black/50 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* A Decade of Results */}
      <section
        className="py-24 md:py-32 px-6 md:px-12 lg:px-24"
        style={{ backgroundColor: NAVY }}
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="max-w-3xl">
            <motion.p
              {...fadeUp}
              className="text-sm uppercase tracking-[0.3em] text-white/50 mb-4 font-medium"
            >
              Track Record
            </motion.p>
            <motion.h2
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] text-white mb-8"
            >
              A Decade of{" "}
              <span className="font-medium">Results</span>
            </motion.h2>
            <motion.p
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg text-white/70 leading-relaxed mb-10"
            >
              We've driven doubled conversion rates, reversed decade-long declines, and delivered record financial quarters at technology companies across SaaS, platforms, marketplaces, and fintech.
            </motion.p>
            <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.3 }}>
              <Link
                to="/case-study"
                className="text-white underline underline-offset-4 hover:text-white/80 transition-colors"
              >
                See our case study &rarr;
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQSchema faqs={faqs} heading="Frequently Asked Questions" />

      {/* CTA */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 border-t border-black/10">
        <div className="max-w-[1400px] mx-auto text-center">
          <motion.h2
            {...fadeUp}
            className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-8"
          >
            Ready to talk?
          </motion.h2>
          <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.1 }}>
            <button
              onClick={() => { trackEvent('contact_click', { source: 'cta_block' }); setIsContactOpen(true); }}
              className="px-8 py-4 bg-[#002178] text-white font-medium hover:scale-105 transition-transform"
            >
              Let's chat
            </button>
          </motion.div>
        </div>
      </section>

      <ContactModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </PageLayout>
  );
};

export default About;
